'use client';

import { useState } from 'react';
import { cn } from '../../lib/utils';

export function SSHKeyGuide() {
  const [keyType, setKeyType] = useState('ed25519');
  const [comment, setComment] = useState('user@hostname');
  const [passphrase, setPassphrase] = useState(true);
  const [copied, setCopied] = useState(false);

  const keyTypes = {
    ed25519: {
      name: 'Ed25519',
      description: 'Modern, schnell und sicher (empfohlen)',
      command: 'ssh-keygen -t ed25519',
      keySize: null,
      pros: ['Sehr sicher', 'Kurze Keys', 'Schnell', 'Moderne Kryptographie'],
      cons: ['Nicht von sehr alten Systemen unterstützt'],
    },
    rsa: {
      name: 'RSA 4096',
      description: 'Bewährt und weitverbreitet',
      command: 'ssh-keygen -t rsa -b 4096',
      keySize: 4096,
      pros: ['Breite Kompatibilität', 'Sehr sicher mit 4096 Bit', 'Etablierter Standard'],
      cons: ['Größere Keys', 'Langsamer als Ed25519'],
    },
    ecdsa: {
      name: 'ECDSA',
      description: 'Elliptic Curve (Alternative)',
      command: 'ssh-keygen -t ecdsa -b 521',
      keySize: 521,
      pros: ['Kleiner als RSA', 'Gute Performance'],
      cons: ['Weniger verbreitet als Ed25519', 'Kontroverse um NSA-Beteiligung'],
    },
  };

  const generateCommand = () => {
    let cmd = keyTypes[keyType].command;
    if (comment) {
      cmd += ` -C "${comment}"`;
    }
    return cmd;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="font-heading font-bold text-2xl mb-4">
        SSH Key Generator Guide
      </h3>
      <p className="text-gray-700 mb-6">
        Schritt-für-Schritt Anleitung zum Generieren sicherer SSH-Schlüssel. Die Generierung erfolgt lokal auf deinem System via Terminal.
      </p>

      {/* Important Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-yellow-800">
          <strong>⚠️ Sicherheitshinweis:</strong> SSH-Schlüssel sollten aus Sicherheitsgründen lokal auf deinem System generiert werden,
          nicht im Browser. Dieses Tool generiert die Befehle für dein Terminal.
        </p>
      </div>

      {/* Key Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-3">
          Schlüssel-Typ wählen
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(keyTypes).map(([key, type]) => (
            <button
              key={key}
              onClick={() => setKeyType(key)}
              className={cn(
                'text-left p-4 rounded-lg border-2 transition-all',
                keyType === key
                  ? 'border-accent bg-accent-light'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              )}
            >
              <div className="font-bold text-lg mb-1">{type.name}</div>
              <div className="text-sm text-gray-600">{type.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Key Details */}
      <div className="mb-6 bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4">
          {keyTypes[keyType].name} Details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-semibold text-green-700 mb-2">Vorteile:</p>
            <ul className="space-y-1">
              {keyTypes[keyType].pros.map((pro, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>{pro}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-orange-700 mb-2">Nachteile:</p>
            <ul className="space-y-1">
              {keyTypes[keyType].cons.map((con, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-orange-600">!</span>
                  <span>{con}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-semibold mb-2">
            Kommentar (z.B. E-Mail-Adresse)
          </label>
          <input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="user@hostname"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Hilft bei der Identifikation des Schlüssels (optional)
          </p>
        </div>

        <div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={passphrase}
              onChange={(e) => setPassphrase(e.target.checked)}
              className="w-5 h-5 text-accent rounded focus:ring-accent cursor-pointer"
            />
            <span className="text-gray-700">
              <strong>Passphrase verwenden</strong> (empfohlen für zusätzliche Sicherheit)
            </span>
          </label>
        </div>
      </div>

      {/* Generated Command */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold">
            Terminal-Befehl
          </label>
          <button
            onClick={() => copyToClipboard(generateCommand())}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
              copied
                ? 'bg-green-500 text-white'
                : 'bg-accent hover:bg-accent-dark text-white'
            )}
          >
            {copied ? '✓ Kopiert!' : 'Kopieren'}
          </button>
        </div>
        <div className="bg-gray-900 text-green-400 p-4 rounded-lg">
          <pre className="text-sm font-mono">
            {generateCommand()}
          </pre>
        </div>
      </div>

      {/* Step-by-Step Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h4 className="font-semibold text-lg mb-4 text-blue-900">
          📋 Schritt-für-Schritt Anleitung
        </h4>
        <ol className="space-y-3 text-sm text-blue-800 list-decimal list-inside">
          <li>
            <strong>Terminal öffnen</strong>
            <p className="ml-6 mt-1 text-blue-700">
              macOS: Terminal.app | Linux: Konsole | Windows: PowerShell oder WSL
            </p>
          </li>
          <li>
            <strong>Befehl ausführen</strong>
            <p className="ml-6 mt-1 text-blue-700">
              Kopiere den generierten Befehl und füge ihn ins Terminal ein
            </p>
          </li>
          <li>
            <strong>Speicherort bestätigen</strong>
            <p className="ml-6 mt-1 text-blue-700">
              Standardpfad: <code className="bg-blue-100 px-1 rounded">~/.ssh/id_{keyType}</code> (mit Enter bestätigen)
            </p>
          </li>
          {passphrase && (
            <li>
              <strong>Passphrase eingeben</strong>
              <p className="ml-6 mt-1 text-blue-700">
                Wähle eine starke Passphrase (mindestens 12 Zeichen empfohlen)
              </p>
            </li>
          )}
          <li>
            <strong>Fertig!</strong>
            <p className="ml-6 mt-1 text-blue-700">
              Dein SSH-Schlüsselpaar wurde erstellt
            </p>
          </li>
        </ol>
      </div>

      {/* Public Key Usage */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <h4 className="font-semibold text-lg mb-4">
          🔑 Public Key verwenden
        </h4>
        <div className="space-y-3 text-sm">
          <div>
            <p className="font-semibold mb-2">1. Public Key anzeigen:</p>
            <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs">
              cat ~/.ssh/id_{keyType}.pub
            </div>
          </div>
          <div>
            <p className="font-semibold mb-2">2. Auf Server hochladen:</p>
            <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs">
              ssh-copy-id user@server
            </div>
            <p className="text-gray-600 mt-2">
              Oder manuell zum Server kopieren und in <code className="bg-gray-200 px-1 rounded">~/.ssh/authorized_keys</code> einfügen
            </p>
          </div>
          <div>
            <p className="font-semibold mb-2">3. Verbindung testen:</p>
            <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs">
              ssh user@server
            </div>
          </div>
        </div>
      </div>

      {/* GitHub/GitLab Integration */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4 text-purple-900">
          💻 GitHub / GitLab Integration
        </h4>
        <ol className="space-y-2 text-sm text-purple-800 list-decimal list-inside">
          <li>
            Public Key kopieren:
            <div className="bg-purple-100 text-purple-900 p-2 rounded font-mono text-xs mt-1 ml-6">
              cat ~/.ssh/id_{keyType}.pub | pbcopy
            </div>
            <p className="text-xs text-purple-600 ml-6 mt-1">(auf Linux: xclip statt pbcopy)</p>
          </li>
          <li>
            <strong>GitHub:</strong> Settings → SSH and GPG keys → New SSH key
          </li>
          <li>
            <strong>GitLab:</strong> Preferences → SSH Keys → Add new key
          </li>
          <li>
            Public Key einfügen und speichern
          </li>
          <li>
            Test: <code className="bg-purple-100 px-1 rounded">ssh -T git@github.com</code>
          </li>
        </ol>
      </div>

      {/* Security Tips */}
      <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4 text-red-900">
          🔒 Sicherheits-Tipps
        </h4>
        <ul className="space-y-2 text-sm text-red-800">
          <li className="flex items-start gap-2">
            <span className="text-red-600 mt-1">→</span>
            <span>
              <strong>Private Key schützen:</strong> Niemals den privaten Schlüssel (ohne .pub) teilen oder online stellen!
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-600 mt-1">→</span>
            <span>
              <strong>Passphrase verwenden:</strong> Schützt den Key bei Diebstahl des Computers
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-600 mt-1">→</span>
            <span>
              <strong>Dateiberechtigungen:</strong> Private Key sollte chmod 600 haben
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-600 mt-1">→</span>
            <span>
              <strong>Backup:</strong> Sichere deine Keys, aber verschlüsselt (z.B. mit VeraCrypt)
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
